import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';

export interface IomiKpis {
  totalOrgs: number;
  activeSubscriptions: number;
  totalUsers: number;
  totalSubscriptionQuantity: number;
  totalQuotes: number;
  totalPdfsGenerated: number;
}

export interface MonthCount {
  month: string;
  count: number;
}

export interface LabeledCount {
  status: string;
  label: string;
  count: number;
}

export interface OrgQuoteData {
  orgId: string;
  orgName: string;
  count: number;
  value: number;
}

export interface OrgSummary {
  id: string;
  name: string;
  isDemo: boolean;
  userCount: number;
  quoteCount: number;
  subscriptionStatus: string;
  createdAt: string;
}

export interface RoleCount {
  role: string;
  count: number;
}

export interface IomiDashboardData {
  kpis: IomiKpis;
  orgGrowth: MonthCount[];
  subscriptionBreakdown: LabeledCount[];
  quotesByOrg: OrgQuoteData[];
  pdfsByMonth: MonthCount[];
  quoteStatusPlatform: LabeledCount[];
  orgList: OrgSummary[];
  usersByRole: RoleCount[];
  pdfMonthlyLimit: number;
}

interface IomiDashboardResponse {
  success: boolean;
  data: IomiDashboardData;
}

export function useIomiDashboard(excludeDemo: boolean) {
  const [data, setData] = useState<IomiDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = excludeDemo ? '?excludeDemo=true' : '';
      const res = await api.get<IomiDashboardResponse>(`/admin/iomi-dashboard${params}`);
      setData(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load IOMI dashboard');
    } finally {
      setLoading(false);
    }
  }, [excludeDemo]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return { data, loading, error, refetch: fetchDashboard };
}
